/* eslint-disable no-unused-vars */
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Lock, Users, FileText, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6"
          >
            <Shield className="w-10 h-10 text-blue-600" />
          </motion.div>
          
          <h1 className="text-5xl font-bold bg-gradient-to-r from-slate-800 to-blue-600 bg-clip-text text-transparent mb-6">
            EventPlanly — Privacy Policy
          </h1>
          <div className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed mb-4">
            <p className="mb-2">
              <span className="font-semibold">Effective Date:</span> September 12, 2025
            </p>
            <p>
              <span className="font-semibold">Last Updated:</span> October 21, 2025
            </p>
          </div>
          <p className="text-slate-600 max-w-3xl mx-auto leading-relaxed">
            EventPlanly ("we", "us", "our") respects your privacy and is committed to protecting your personal information. 
            This Privacy Policy explains how we collect, use, store, and share your information when you use our mobile 
            application and services. By using EventPlanly, you consent to this Privacy Policy.
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-8"
        >
          {/* Information We Collect */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">
                  1. Information We Collect
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  We collect information in compliance with the <strong>Information Technology Act, 2000</strong>, 
                  <strong> SPDI Rules, 2011</strong>, and <strong>Digital Personal Data Protection Act, 2023</strong>. 
                  The types of data include:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <h3 className="font-semibold text-slate-800 mb-4">From Users (Customers):</h3>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Name and contact details (email, phone number)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Event preferences and booking details</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Location data to provide nearby services</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Wallet transactions within the App</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                    <h3 className="font-semibold text-slate-800 mb-4">From Partners (Service Providers):</h3>
                    <ul className="space-y-2 text-slate-600">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Identity details (name, contact info, GST number if applicable)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Business information (service types, pricing, photos, policies)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Bank details (only for withdrawal processing)</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-slate-700 font-medium">
                    <strong>We do not collect sensitive financial information such as credit/debit card numbers or UPI IDs.</strong>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Purpose of Collection */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">
                  2. Purpose of Collection
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Your data is used for legitimate business purposes, including:
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Facilitating bookings between users and partners</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Personalizing service recommendations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Providing secure payments and withdrawals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Sending notifications, invoices, and updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Compliance with Indian legal obligations</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Consent */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">3. Consent</h2>
            <p className="text-slate-600 leading-relaxed">
              By registering or using EventPlanly, you consent to data collection, storage, and processing as per this policy. 
              You may withdraw consent anytime by deleting your account, subject to legal and contractual obligations.
            </p>
          </motion.div>

          {/* Data Sharing */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">
                  4. Data Sharing
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We do not sell or trade your personal data. Data may be shared with:
                </p>
                
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Partners</strong> - to complete service requests</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Third-party vendors</strong> - providing hosting, analytics, or payment processing (bound by confidentiality)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Government authorities</strong> - when legally required</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Data Security */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Lock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">
                  5. Data Security
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  We implement <strong>Reasonable Security Practices and Procedures</strong> under Section 43A of the IT Act. 
                  Measures include:
                </p>
                
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Encryption of sensitive data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Access restrictions based on user roles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Regular audits to prevent unauthorized access</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Data Retention */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">6. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain data only as long as necessary for providing services, legal compliance, and dispute resolution. 
              Financial and tax-related records may be kept for up to 8 years as per Indian law.
            </p>
          </motion.div>

          {/* Your Rights */}
          <motion.div
            variants={fadeInUp}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-lg"
          >
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-3">
              <Users className="w-6 h-6" />
              7. Your Rights
            </h2>
            <p className="text-blue-100 mb-6 leading-relaxed">
              Under Indian privacy laws, you have the right to:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Access and correct your data within the App",
                "Request account deletion and data erasure", 
                "Withdraw consent for processing",
                "Raise grievances with our Data Protection Officer"
              ].map((right, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center gap-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm"
                >
                  <div className="w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                  <span className="text-sm">{right}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Children's Privacy */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              EventPlanly is not intended for children below 18 years of age. We do not knowingly collect personal 
              information from minors. If discovered, such data will be deleted promptly.
            </p>
          </motion.div>

          {/* Third-Party Links */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">9. Third-Party Links</h2>
            <p className="text-slate-600 leading-relaxed">
              Our App may contain links to external websites. We are not responsible for their privacy practices. 
              Users should review third-party policies before sharing data.
            </p>
          </motion.div>

          {/* Governing Law */}
          {/* <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">11. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              This policy is governed by the laws of India, including the Information Technology Act, 2000, SPDI Rules, 2011, 
              and DPDP Act, 2023. Disputes will be subject to the jurisdiction of courts in <strong>Houston, Texas, USA</strong>, 
              under our parent company <strong>ITDesks</strong>.
            </p>
          </motion.div> */}

          {/* Changes to Policy */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
          >
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">12. Changes to Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be reflected with an updated "Last Updated" date. 
              Continued use of the App constitutes acceptance of changes.
            </p>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            variants={fadeInUp}
            className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-3">
                13. Contact Us
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                For any privacy-related queries:
              </p>
              
              <div className="flex flex-col gap-4 justify-center items-center">
                <div className="flex items-center gap-2 text-slate-700">
                  <Mail size={18} className="text-blue-600" />
                  <span className="font-medium">info@itdesks.co</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Phone size={18} className="text-green-600" />
                  <span className="font-medium">+1 (510) 520-7318</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin size={18} className="text-purple-600" />
                  <span className="font-medium">Houston, TX, 77493, US</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            variants={fadeInUp}
            className="text-center py-8"
          >
            <div className="inline-flex flex-col items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl px-8 py-6 shadow-lg">
              <div className="text-lg font-semibold">
                © EventPlanly — A Child Company of ITdesks. All rights reserved.
              </div>
              <div className="flex items-center gap-2 text-blue-100 text-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Last updated: October 21, 2025</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;